import { DeleteOutline } from '@mui/icons-material';
import {
  Box,
  Button,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';
import { useState } from 'react';

import {
  BeverageType,
  BeverageTypeValues,
} from '@/domain/impact-co2/entities/question.entity';
import {
  ValuesCateringBeverage,
  VenueCo2MonthValues,
} from '@/domain/project-venue-month-declaration/entities/project-venue-month-declaration';
import { FSPrimaryButton } from '@/ui/components/button/FSPrimaryButton';
import { FSTextField } from '@/ui/components/input/FSTextField';
import { thStyle } from '@/ui/components/table/Table.utils';
import { translate } from '@/ui/i18n';
import { ColorsRaw } from '@/ui/theme/colors-raw';

import { QuizEdition, QuizNotEdition } from '../Co2MonthQuizComponents';
import { useMonthDeclarationDisableSubmit } from './hooks/useMonthDeclarationDisableSubmit';

export function CateringBeverageForm({
  onEdit,
  co2MonthValues,
}: {
  onEdit: (co2Values: VenueCo2MonthValues) => void;
  co2MonthValues: VenueCo2MonthValues;
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [values, setValues] = useState(
    structuredClone(co2MonthValues.cateringBeverage),
  );

  const { disableSubmitActions } = useMonthDeclarationDisableSubmit();

  const addAnswer = (value: BeverageType) => {
    setValues([
      ...values,
      { beverageType: value, beverageCount: 0, sector: 'PUBLIC' },
    ]);
  };

  const updateQuantity = (value: string, index: number) => {
    const newValues = [...values];
    newValues[index].beverageCount = Number(value);
    if (!isNaN(newValues[index].beverageCount)) {
      setValues(newValues);
    }
  };

  const removeAnswer = (index: number) => {
    const newValues = [...values];
    newValues.splice(index, 1);
    setValues(newValues);
  };

  const handleEdit = () => {
    onEdit({ ...co2MonthValues, cateringBeverage: values });
    setIsEditing(false);
  };

  const handleRemoveClick = () => {
    setIsEditing(false);
  };

  return (
    <div>
      {isEditing ? (
        <QuizEdition
          removeFromEditing={handleRemoveClick}
          title={translate(
            'ProjectsVenue.MonthCo2Quiz.CateringBeverage.how-many-beverages',
          )}
          form={
            <Form
              onSubmit={handleEdit}
              values={values}
              disableSubmit={disableSubmitActions}
              addAnswer={addAnswer}
              updateQuantity={updateQuantity}
              removeAnswer={removeAnswer}
            />
          }
        />
      ) : (
        <QuizNotEdition
          addToEditing={() => setIsEditing(true)}
          answeredValues={values.map((value) => {
            return `${translate(`ImpactQuestion.CATERING_BEVERAGE.beverageTypeValues.${value.beverageType}`)} : ${value.beverageCount}`;
          })}
          title={translate(
            'ProjectsVenue.MonthCo2Quiz.CateringBeverage.how-many-beverages',
          )}
          isAnswered={values.length > 0}
        />
      )}
    </div>
  );
}

function Form({
  addAnswer,
  updateQuantity,
  removeAnswer,
  disableSubmit,
  onSubmit,
  values,
}: {
  onSubmit: () => void;
  addAnswer: (value: BeverageType) => void;
  disableSubmit: boolean;
  updateQuantity: (value: string, index: number) => void;
  removeAnswer: (index: number) => void;
  values: ValuesCateringBeverage;
}) {
  const [selected, setSelected] = useState<BeverageType | ''>('');

  const getSelectValues = () => {
    const selectedItems = values.map((value) => value.beverageType);

    return selectedItems
      ? BeverageTypeValues.filter((acc) => !selectedItems.includes(acc))
      : BeverageTypeValues;
  };

  const addType = () => {
    if (selected) {
      addAnswer(selected);
      setSelected('');
    }
  };

  const handleChangeQuantity = (
    e: React.ChangeEvent<HTMLInputElement>,
    index: number,
  ) => {
    updateQuantity(e.target.value, index);
  };

  const handleSubmit = () => {
    onSubmit();
  };

  return (
    <Stack>
      <Table align="right">
        <TableHead>
          <TableRow>
            <TableCell style={thStyle}>
              {translate('ImpactQuestion.CATERING_BEVERAGE.beverageTypeTitle')}
            </TableCell>
            <TableCell style={thStyle}>
              {translate('ProjectsVenue.MonthCo2Quiz.quantity')}
            </TableCell>
            <TableCell style={thStyle} />
          </TableRow>
        </TableHead>
        <TableBody>
          {values?.map((value, index) => (
            <TableRow key={'selected' + index}>
              <TableCell>
                {translate(
                  `ImpactQuestion.CATERING_BEVERAGE.beverageTypeValues.${value.beverageType}`,
                )}
              </TableCell>
              <TableCell sx={{ py: 0 }}>
                <FSTextField
                  sx={{ width: '80px' }}
                  type="number"
                  textAlign="right"
                  value={value.beverageCount}
                  onChange={(e) => handleChangeQuantity(e, index)}
                />
              </TableCell>
              <TableCell sx={{ px: 0 }}>
                <Box display="flex" justifyContent="end">
                  <Button onClick={() => removeAnswer(index)}>
                    <DeleteOutline sx={{ color: ColorsRaw.basic.White }} />
                  </Button>
                </Box>
              </TableCell>
            </TableRow>
          ))}
          <TableRow>
            <TableCell colSpan={4}>
              <Box display={'flex'} alignItems={'center'}>
                <FSTextField
                  select
                  label={translate(
                    'ImpactQuestion.CATERING_BEVERAGE.beverageTypeTitle',
                  )}
                  value={selected}
                  onChange={(e) => setSelected(e.target.value as BeverageType)}
                  sx={{ width: '100%' }}
                >
                  {getSelectValues().map((value) => (
                    <MenuItem key={value} value={value}>
                      {translate(
                        `ImpactQuestion.CATERING_BEVERAGE.beverageTypeValues.${value}`,
                      )}
                    </MenuItem>
                  ))}
                </FSTextField>

                <FSPrimaryButton sx={{ ml: 1 }} onClick={addType}>
                  {translate('Form.add')}
                </FSPrimaryButton>
              </Box>
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>

      <Box marginTop={2}>
        <FSPrimaryButton
          type="submit"
          disabled={disableSubmit}
          onClick={handleSubmit}
        >
          {translate('Form.save')}
        </FSPrimaryButton>
      </Box>
    </Stack>
  );
}
